import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { EyeIcon, EyeCloseIcon } from '../../icons';
import PageMeta from '../../components/common/PageMeta';
import AuthLayout from './AuthPageLayout';
import { pillButtonClass, pillFieldClass, pillInputClass } from './authControls';
import { useAuth } from '../../contexts/useAuth';
import { businessApi } from '../../config/api';
import { clientErrorServerMessage } from '../../config/apiError';
import { subscriptionApi, type Plan } from '../../services/subscriptionApi';
import { isSubscribeEnabled } from '../../config/features';
import { formatAudMinor, seatTotalMinor } from '../../utils/seatPricing';

/**
 * Registration, on the brand design (Claude Design `Register.dc.html`): two steps — your details,
 * then choose plan — on the same gradient/skyline shell as sign-in.
 *
 * NOTHING is created until the final button. Step 1 only validates and holds the details in
 * state; the account, the business and the plan choice are all committed together by step 2's
 * CTA. That is deliberate — creating the account on step 1 left anyone who bailed on the plan
 * step with a half-made login they never asked for.
 *
 * The one case that still arrives here already authenticated is someone resuming an older
 * abandoned signup (logged in, no business). `accountCreated` marks that: they skip the register
 * call and only get a business (see the resume effect below).
 *
 * Three deliberate departures from the mockup, each because the mockup pre-dates a shipped
 * decision:
 *
 *  - No council picking. The old step 3 made you choose up to 2 councils. Councils are free and
 *    unlimited now, so the business is created on the all-councils model and the customer removes
 *    what they don't want from /business-details.
 *  - No trial framing. The mockup's Intelligence card promises a "14-day free trial" and its CTA
 *    reads START 14-DAY FREE TRIAL; trials were switched off deliberately, so neither appears.
 *  - The Intelligence price is read from Stripe, not from the mockup's hard-coded $29.99. See
 *    seatPricing.ts: a second price table in the client is how a customer gets quoted one number
 *    and charged another. Choosing it hands off to Stripe Checkout with the seat count as the
 *    quantity, once `intelligenceAvailable` says both the switch and the price are there.
 *
 * The mockup's step 1 also drops the address/phone/suburb fields the old wizard collected. That is
 * the design's intent ("takes under a minute"), so the success screen sends people to
 * /business-details, which is the page built to collect them.
 */

type Step = 0 | 1;
type PlanChoice = 'marketplace' | 'intelligence';

const MAX_SEATS = 10;

// Split a full name into first/last. First whitespace-separated token is the first name;
// everything after it is the last name ("Matt O'Brien Jr" -> "Matt" / "O'Brien Jr"). The server
// requires a first name only, so a single-word name is fine.
const splitName = (full: string): { firstName: string; lastName: string } => {
    const parts = full.trim().split(/\s+/).filter(Boolean);
    return { firstName: parts.shift() ?? '', lastName: parts.join(' ') };
};

// Rendered by BOTH /signup and /freetrial — the marketing site links to /freetrial, so that route
// must keep working. It no longer promises a trial. The paid plan is wired end to end but stays
// locked until isSubscribeEnabled() and the server's Features:Subscriptions:Enabled are both on —
// see the comment on isSubscribeEnabled for what that involves.
const SignUp: React.FC = () => {
    const navigate = useNavigate();
    const { register, refreshUser, user, isLoading: authLoading } = useAuth();

    // Prefill from URL params (e.g. the marketing-site box links to
    // /freetrial?name=…&business=…&email=…). useSearchParams handles decoding correctly:
    // `+`/`%20` -> space, `%2B` -> literal `+` in emails. All three params are optional.
    const [searchParams] = useSearchParams();
    const prefill = useRef({
        name: (searchParams.get('name') ?? '').trim(),
        business: (searchParams.get('business') ?? '').trim(),
        email: (searchParams.get('email') ?? '').trim(),
    }).current;

    const [step, setStep] = useState<Step>(0);
    const [completed, setCompleted] = useState(false);
    const [accountCreated, setAccountCreated] = useState(false);
    const [savingBusiness, setSavingBusiness] = useState(false);
    const [accountError, setAccountError] = useState<string | null>(null);
    const [businessError, setBusinessError] = useState<string | null>(null);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(true);

    const [account, setAccount] = useState({
        fullName: prefill.name,
        businessName: prefill.business,
        email: prefill.email,
        password: '',
        confirmPassword: '',
    });

    const subscribable = isSubscribeEnabled();
    const [plan, setPlan] = useState<PlanChoice>('marketplace');
    const [seats, setSeats] = useState(1);
    const [plans, setPlans] = useState<Plan[]>([]);

    // Resume an abandoned sign-up. This page no longer creates an account before the end, but a
    // user from an earlier signup can still arrive logged in with no business — and nothing else
    // creates one for them. `accountCreated` marks that: the finish below skips registration
    // rather than trying to register them twice, and the details step asks only for the business
    // name, which is the one thing still missing. They stay ON that step for exactly that reason —
    // skipping them to the plan step would commit a business with an empty name.
    const resumeCheckedRef = useRef(false);
    useEffect(() => {
        if (resumeCheckedRef.current || authLoading || !user) return;
        resumeCheckedRef.current = true;
        businessApi.getMyBusinessOrNull()
            .then((biz) => {
                if (biz) return; // already set up; leave them where they are
                setAccountCreated(true);
                setAccount((a) => ({ ...a, email: a.email || user.email || '' }));
            })
            .catch(() => { /* transient; the normal flow still works */ });
    }, [authLoading, user]);

    // Real Stripe prices, so the Intelligence card quotes what Stripe will actually charge. The
    // endpoint is anonymous, and a failure just means no price is shown — never a guessed one.
    useEffect(() => {
        subscriptionApi.getPlans()
            .then(setPlans)
            .catch(() => setPlans([]));
    }, []);

    const intelligencePlan = useMemo(
        () => plans.find((p) => p.key === 'intelligence'),
        [plans],
    );

    /**
     * Whether Intelligence can actually be bought right now — BOTH conditions, not just the flag.
     * The price has to be configured and retrievable (GET /api/stripe/plans only returns plans
     * whose Stripe:PriceIds entry resolves), or the card would be selectable with nothing behind
     * it and checkout would fail after the account had already been created. Tying it to the live
     * price also means each environment lights up on its own as its ids land, with nothing here
     * to remember to change.
     */
    const intelligenceAvailable = subscribable && intelligencePlan !== undefined;

    // Whether finishing will redirect to Stripe Checkout rather than complete signup directly —
    // drives the finish button's label (CONTINUE TO PAYMENT vs CREATE FREE ACCOUNT). Mirrors the
    // condition in handleFinish that actually creates the checkout session.
    const goingToCheckout = plan === 'intelligence' && intelligenceAvailable;

    /** What Stripe charges for `count` seats, or null when we have no live price to quote. */
    const intelligenceTotal = (count: number): string | null => {
        if (!intelligencePlan) return null;
        return formatAudMinor(seatTotalMinor(
            intelligencePlan.unitAmountMinor,
            intelligencePlan.tiersMode,
            intelligencePlan.tiers,
            count,
        ));
    };

    const { firstName } = splitName(account.fullName);

    const businessNamed = account.businessName.trim() !== '';

    // A resumed signup already has the account, so its fields are not asked for again and must not
    // gate the step — the business name is the only thing still outstanding.
    const detailsValid = accountCreated
        ? businessNamed
        : firstName !== '' &&
          businessNamed &&
          /\S+@\S+\.\S+/.test(account.email) &&
          account.password.length >= 8 &&
          account.password === account.confirmPassword;

    // Step 1 only moves to the plan step. It creates nothing — see the file comment.
    const handleContinue = (e: React.FormEvent) => {
        e.preventDefault();
        if (!detailsValid) return;
        setAccountError(null);
        setStep(1);
    };

    // The one commit point: account, then business, then the success screen.
    const handleFinish = async (e: React.FormEvent) => {
        e.preventDefault();
        if (savingBusiness || !termsAccepted) return;
        if (!detailsValid) { setStep(0); return; }
        setAccountError(null);
        setBusinessError(null);
        setSavingBusiness(true);
        try {
            if (!accountCreated) {
                const { firstName: first, lastName } = splitName(account.fullName);
                // Claim the resume check before registering: the effect above watches `user`, and
                // without this it would fire for the account we are creating right here.
                resumeCheckedRef.current = true;
                const result = await register({
                    firstName: first,
                    lastName,
                    email: account.email.trim(),
                    password: account.password,
                });
                if (!result.ok) {
                    // Whatever is wrong (a taken email, usually) is a field on step 1, so send
                    // them back to it rather than showing the error next to a plan card.
                    setAccountError(result.message || 'Could not create your account. Please try again.');
                    setStep(0);
                    return;
                }
                setAccountCreated(true);
            }
            await businessApi.initMyBusiness();
            await businessApi.updateMyBusiness({
                name: account.businessName.trim(),
                email: account.email.trim(),
                acceptTerms: true,
                // The all-councils model: everyone starts receiving every council area and removes
                // what they don't want from /business-details. Councils are not a purchase.
                allCouncilAreas: true,
            });
            // Refresh the auth context so the new business populates activeBusiness immediately.
            await refreshUser();

            if (plan === 'intelligence' && intelligenceAvailable && intelligencePlan) {
                // Hand off to Stripe Checkout. `seats` is the quantity — people, not councils —
                // and councilAreas is deliberately null: the server only writes that field when
                // it is non-null, and this business was just put on the all-councils model.
                const { url } = await subscriptionApi.createCheckoutSession(
                    intelligencePlan.priceId, null, false, undefined, seats,
                );
                window.location.assign(url);
                return; // leaving the page — don't flash the success screen behind the redirect
            }

            setCompleted(true);
        } catch (err) {
            // The account exists by now. Only a 4xx message the server wrote for people (a
            // refused coupon, a field it rejected) beats the reassuring fallback — never a 5xx,
            // a network failure, or an unexpected error's text.
            setBusinessError(clientErrorServerMessage(err)
                ?? 'Your account was created, but the next step failed. Please try again.');
        } finally {
            setSavingBusiness(false);
        }
    };

    return (
        <>
            <PageMeta title="Sign Up" description="Create your DAconnect account" />
            <AuthLayout>
                <div className="w-full max-w-[480px]">
                    {completed ? (
                        <CompleteScreen
                            firstName={firstName}
                            onBusinessDetails={() => navigate('/business-details')}
                            // "/dashboard", not "/": the marketplace took "/" as the public front
                            // door, so sending someone there at the end of signup ejected a
                            // brand-new customer out of the app and onto the marketing site.
                            onDashboard={() => navigate('/dashboard')}
                        />
                    ) : (
                        <>
                            <StepChips step={step} />

                            {step === 0 ? (
                                <form onSubmit={handleContinue} className="flex flex-col items-center">
                                    <h1 className="text-center font-display text-[28px] leading-[1.2] font-medium text-ink md:text-[30px] short:text-[26px] shorter:text-[23px]">
                                        <b className="font-semibold text-sidebar dark:text-leaf">{accountCreated ? 'Finish' : 'Create'}</b> your account
                                    </h1>
                                    <p className="mt-2 mb-6 text-center text-[13.5px] text-ink-muted short:mt-1 short:mb-3.5 shorter:mb-2.5">
                                        {accountCreated
                                            ? <>Signed in as {account.email || 'your account'} — just your business name to go</>
                                            : 'Free to join · takes under a minute'}
                                    </p>

                                    <div className="flex w-full flex-col gap-3.5 short:gap-2 shorter:gap-1.5">
                                        {accountError && <ErrorPill>{accountError}</ErrorPill>}

                                        {/* Hidden for a resumed signup: this account already exists, so asking for a
                                            name, an email and a password it will not use would be a lie. */}
                                        {!accountCreated && (
                                        <label className={pillFieldClass(account.fullName.length > 0)}>
                                            <FieldIcon filled={account.fullName.length > 0}>
                                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                                <circle cx="12" cy="7" r="4" />
                                            </FieldIcon>
                                            <input
                                                id="fullName"
                                                aria-label="Full name"
                                                autoComplete="name"
                                                placeholder="Full name"
                                                className={pillInputClass}
                                                value={account.fullName}
                                                onChange={(e) => setAccount({ ...account, fullName: e.target.value })}
                                            />
                                        </label>
                                        )}

                                        <label className={pillFieldClass(account.businessName.length > 0)}>
                                            <FieldIcon filled={account.businessName.length > 0}>
                                                <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                            </FieldIcon>
                                            <input
                                                id="businessName"
                                                aria-label="Business name"
                                                autoComplete="organization"
                                                placeholder="Business name"
                                                className={pillInputClass}
                                                value={account.businessName}
                                                onChange={(e) => setAccount({ ...account, businessName: e.target.value })}
                                            />
                                        </label>

                                        {/* Same reason as above: an existing account is not re-created, so it is not re-asked for. */}
                                        {!accountCreated && (
                                        <>
                                        <label className={pillFieldClass(account.email.length > 0)}>
                                            <FieldIcon filled={account.email.length > 0}>
                                                <rect x="2" y="4" width="20" height="16" rx="3" />
                                                <path d="m22 7-10 6L2 7" />
                                            </FieldIcon>
                                            <input
                                                id="email"
                                                type="email"
                                                aria-label="Email address"
                                                autoComplete="email"
                                                placeholder="Email address"
                                                className={pillInputClass}
                                                value={account.email}
                                                onChange={(e) => setAccount({ ...account, email: e.target.value })}
                                            />
                                        </label>

                                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                            <label className={pillFieldClass(account.password.length > 0)}>
                                                <FieldIcon filled={account.password.length > 0}>
                                                    <rect x="3" y="11" width="18" height="11" rx="3" />
                                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                                </FieldIcon>
                                                <input
                                                    id="password"
                                                    type={showPassword ? 'text' : 'password'}
                                                    aria-label="Password"
                                                    autoComplete="new-password"
                                                    placeholder="Password"
                                                    className={pillInputClass}
                                                    value={account.password}
                                                    onChange={(e) => setAccount({ ...account, password: e.target.value })}
                                                />
                                                <RevealButton shown={showPassword} onToggle={() => setShowPassword(!showPassword)} />
                                            </label>

                                            <label className={pillFieldClass(account.confirmPassword.length > 0)}>
                                                <FieldIcon filled={account.confirmPassword.length > 0}>
                                                    <rect x="3" y="11" width="18" height="11" rx="3" />
                                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                                </FieldIcon>
                                                <input
                                                    id="confirmPassword"
                                                    type={showConfirm ? 'text' : 'password'}
                                                    aria-label="Confirm password"
                                                    autoComplete="new-password"
                                                    placeholder="Confirm password"
                                                    className={pillInputClass}
                                                    value={account.confirmPassword}
                                                    onChange={(e) => setAccount({ ...account, confirmPassword: e.target.value })}
                                                />
                                                <RevealButton shown={showConfirm} onToggle={() => setShowConfirm(!showConfirm)} />
                                            </label>
                                        </div>

                                        <p className="px-1.5 text-[12px] text-ink-faint">
                                            {account.confirmPassword !== '' && account.password !== account.confirmPassword
                                                ? <span className="text-red-600 dark:text-red-400">Passwords do not match</span>
                                                : 'At least 8 characters'}
                                        </p>
                                        </>
                                        )}

                                        <button type="submit" disabled={!detailsValid} className={pillButtonClass}>
                                            CONTINUE →
                                        </button>

                                        {!accountCreated && (
                                            <p className="mt-1 text-center text-[13.5px] text-ink-muted">
                                                Already have an account?{' '}
                                                <a href="/login" className="font-bold text-sidebar hover:underline dark:text-leaf">Sign in</a>
                                            </p>
                                        )}
                                    </div>
                                </form>
                            ) : (
                                <form onSubmit={handleFinish} className="flex flex-col items-center">
                                    <h1 className="text-center font-display text-[28px] leading-[1.2] font-medium text-ink md:text-[30px] short:text-[26px] shorter:text-[23px]">
                                        <b className="font-semibold text-sidebar dark:text-leaf">Choose</b> your plan
                                    </h1>
                                    <p className="mt-1.5 mb-4.5 text-center text-[13.5px] text-ink-muted">
                                        Start free, or unlock the DA engine · change anytime
                                    </p>

                                    <div className="flex w-full flex-col gap-2.5">
                                        {businessError && <ErrorPill>{businessError}</ErrorPill>}

                                        <PlanCard
                                            selected={plan === 'marketplace'}
                                            onSelect={() => setPlan('marketplace')}
                                            title="Marketplace"
                                            badge={<span className="rounded-full bg-border px-2 py-0.5 text-[9.5px] font-bold text-icon dark:bg-surface/15">FREE</span>}
                                            description="Verified listing · unlimited enquiries · project gallery"
                                        />

                                        <PlanCard
                                            selected={plan === 'intelligence'}
                                            // Locked until Features:Subscriptions:Enabled is on. The server 404s
                                            // checkout regardless, so an unlocked card would only ever produce an
                                            // error at the end of a signup.
                                            disabled={!intelligenceAvailable}
                                            onSelect={() => setPlan('intelligence')}
                                            title="Intelligence"
                                            ribbon={intelligenceAvailable ? 'RECOMMENDED' : 'COMING SOON'}
                                            badge={
                                                intelligenceTotal(1) && (
                                                    <span className="text-[12.5px] font-bold text-sidebar dark:text-leaf">
                                                        {intelligenceTotal(1)}<span className="font-medium text-ink-faint">/mo</span>
                                                    </span>
                                                )
                                            }
                                            description="Everything in Marketplace + matched DA alerts, every council area, development map"
                                        >
                                            {plan === 'intelligence' && (
                                                <div className="mt-2.5 flex items-center justify-between border-t border-border-inner pt-2.5">
                                                    <span className="text-[12px] text-ink-muted">Team members</span>
                                                    <span className="inline-flex items-center gap-2.5">
                                                        <SeatButton label="Remove a seat" onClick={() => setSeats((s) => Math.max(1, s - 1))}>−</SeatButton>
                                                        <span className="text-sm font-bold text-ink" aria-live="polite">{seats}</span>
                                                        <SeatButton label="Add a seat" onClick={() => setSeats((s) => Math.min(MAX_SEATS, s + 1))}>+</SeatButton>
                                                        {intelligenceTotal(seats) && (
                                                            <span className="ml-1 text-[13px] font-bold text-sidebar dark:text-leaf">
                                                                {intelligenceTotal(seats)}/mo
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            )}
                                        </PlanCard>

                                        <label className="flex cursor-pointer items-start gap-2.5 px-1.5 py-0.5 text-[12.5px] leading-[1.5] text-ink-muted">
                                            <input
                                                type="checkbox"
                                                checked={termsAccepted}
                                                onChange={(e) => setTermsAccepted(e.target.checked)}
                                                className="mt-0.5 size-4 rounded accent-leaf"
                                            />
                                            <span>
                                                I agree to the{' '}
                                                <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-bold text-sidebar hover:underline dark:text-leaf">Terms of Service</a>
                                                {' '}and{' '}
                                                <a href="/terms" target="_blank" rel="noopener noreferrer" className="font-bold text-sidebar hover:underline dark:text-leaf">Privacy Policy</a>
                                            </span>
                                        </label>

                                        <button type="submit" disabled={savingBusiness || !termsAccepted} className={pillButtonClass}>
                                            {savingBusiness ? (
                                                <span className="inline-flex items-center justify-center gap-2">
                                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                                    {goingToCheckout ? 'SETTING UP…' : 'CREATING ACCOUNT…'}
                                                </span>
                                            ) : (goingToCheckout ? 'CONTINUE TO PAYMENT' : 'CREATE FREE ACCOUNT')}
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => setStep(0)}
                                            /* Locked while the commit is in flight: the account and business are
                                               being created right now, and stepping back mid-way lands you on a
                                               form whose fields no longer decide anything. */
                                            disabled={savingBusiness}
                                            className="mt-1 text-center text-[13.5px] font-semibold text-ink-muted transition
                                                       hover:text-ink disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-ink-muted"
                                        >
                                            ← Back to details
                                        </button>
                                    </div>
                                </form>
                            )}
                        </>
                    )}
                </div>
            </AuthLayout>
        </>
    );
};

/** "1 — Your details ——— 2 Choose plan", with step 1 ticking over to a green ✓ once it's done. */
const StepChips: React.FC<{ step: Step }> = ({ step }) => {
    const dot = 'grid size-7 place-items-center rounded-full text-[12.5px] font-bold';
    return (
        <div className="mb-5.5 flex items-center justify-center gap-2.5 short:mb-3 shorter:mb-2">
            <span className={`${dot} ${step === 0 ? 'bg-sidebar text-white' : 'bg-leaf-pill text-sidebar'}`}>
                {step === 0 ? '1' : '✓'}
            </span>
            <span className="text-[12.5px] font-bold text-sidebar dark:text-leaf">Your details</span>
            <span className={`h-0.5 w-11 ${step === 1 ? 'bg-leaf' : 'bg-leaf/30'}`} />
            <span className={`${dot} ${step === 1 ? 'bg-sidebar text-white' : 'bg-surface text-ink-faint'}`}>2</span>
            <span className={`text-[12.5px] font-bold ${step === 1 ? 'text-sidebar dark:text-leaf' : 'text-ink-faint'}`}>
                Choose plan
            </span>
        </div>
    );
};

/** One selectable plan card, with the design's radio circle on the right. */
const PlanCard: React.FC<{
    selected: boolean;
    disabled?: boolean;
    onSelect: () => void;
    title: string;
    badge?: React.ReactNode;
    ribbon?: string;
    description: string;
    children?: React.ReactNode;
}> = ({ selected, disabled = false, onSelect, title, badge, ribbon, description, children }) => (
    <div
        role="radio"
        aria-checked={selected}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && onSelect()}
        onKeyDown={(e) => {
            if (disabled) return;
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); }
        }}
        className={`relative flex items-center justify-between rounded-[18px] border bg-surface px-5 py-4.5 transition
            ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
            ${selected ? 'border-leaf shadow-[0_6px_20px_rgba(22,163,74,.15)]' : 'border-leaf/30 dark:border-leaf/20'}
            dark:bg-surface/10`}
    >
        {ribbon && (
            <span className="absolute -top-2.5 right-4 rounded-full bg-leaf-pill px-2.5 py-[3px] text-[9px] font-bold tracking-[.05em] text-sidebar">
                {ribbon}
            </span>
        )}
        <div className="min-w-0">
            <div className="flex items-center gap-2">
                <span className="text-[15px] font-bold text-ink">{title}</span>
                {badge}
            </div>
            <p className="mt-1 text-[12.5px] leading-[1.5] text-ink-muted">{description}</p>
            {children}
        </div>
        <span
            aria-hidden="true"
            className={`ml-3.5 size-5 shrink-0 rounded-full ${selected ? 'border-5 border-leaf-pill bg-sidebar' : 'border-[1.5px] border-leaf/30'}`}
        />
    </div>
);

/** The −/+ circles on the seat stepper. */
const SeatButton: React.FC<{ label: string; onClick: () => void; children: React.ReactNode }> = ({ label, onClick, children }) => (
    <button
        type="button"
        aria-label={label}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        className="grid size-6.5 place-items-center rounded-full border-[1.5px] border-leaf/30 text-sm font-bold text-sidebar dark:text-leaf"
    >
        {children}
    </button>
);

/** The pale-grey field icon, greening once the field has something in it. */
const FieldIcon: React.FC<{ filled: boolean; children: React.ReactNode }> = ({ filled, children }) => (
    <svg
        width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
        className={`shrink-0 ${filled ? 'text-sidebar dark:text-leaf' : 'text-ink-faint'}`}
    >
        {children}
    </svg>
);

const RevealButton: React.FC<{ shown: boolean; onToggle: () => void }> = ({ shown, onToggle }) => (
    <button type="button" onClick={onToggle} aria-label={shown ? 'Hide password' : 'Show password'} className="shrink-0 cursor-pointer">
        {shown ? <EyeIcon className="size-5 fill-ink-faint" /> : <EyeCloseIcon className="size-5 fill-ink-faint" />}
    </button>
);

const ErrorPill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div role="alert" className="rounded-3xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-300">
        {children}
    </div>
);

/**
 * Success screen. The primary action is /business-details rather than the dashboard: the design's
 * short form does not collect an address, phone or trading details, and that page is where they go.
 */
const CompleteScreen: React.FC<{ firstName: string; onBusinessDetails: () => void; onDashboard: () => void }> = ({ firstName, onBusinessDetails, onDashboard }) => (
    <div className="flex flex-col items-center text-center">
        <div className="mb-5 grid size-14 place-items-center rounded-full bg-leaf-pill text-2xl text-sidebar">✓</div>
        <h1 className="font-display text-[28px] leading-[1.2] font-medium text-ink md:text-[30px] short:text-[26px] shorter:text-[23px]">
            <b className="font-semibold text-sidebar dark:text-leaf">Welcome aboard</b>{firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="mt-2.5 mb-6 max-w-[400px] text-[13.5px] leading-[1.6] text-ink-muted">
            Your account is ready. Add your business details so customers can find you in the
            marketplace — and check your inbox to verify your email when you get a chance.
        </p>
        <div className="flex w-full flex-col gap-2.5">
            <button type="button" onClick={onBusinessDetails} className={pillButtonClass}>
                ADD YOUR BUSINESS DETAILS
            </button>
            <button type="button" onClick={onDashboard} className="text-[13.5px] font-semibold text-ink-muted hover:text-ink">
                Skip for now — go to dashboard
            </button>
        </div>
    </div>
);

export default SignUp;
